import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useLocation } from "react-router-dom";
import { Package, ShoppingCart, Star, BarChart2, Plus, ArrowRight } from "lucide-react";
import AdminAnalytics from "./AdminAnalytics";
import DashboardStats from "@/components/admin/DashboardStats";

interface DashboardCardProps {
  title: string;
  description: string;
  buttonText: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const DashboardCard = ({
  title,
  description,
  buttonText,
  icon,
  onClick,
}: DashboardCardProps) => (
  <Card className="flex flex-col h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-lg bg-primary/10 text-primary flex-shrink-0">
          {icon}
        </div>
        <CardTitle className="text-lg sm:text-xl leading-tight">{title}</CardTitle>
      </div>
      <CardDescription className="text-sm sm:text-base text-gray-600 dark:text-gray-300 line-clamp-2">
        {description}
      </CardDescription>
    </CardHeader>
    <CardContent className="mt-auto pt-0">
      <Button 
        onClick={onClick} 
        variant="outline"
        className="w-full justify-between group btn-touch text-sm sm:text-base"
      >
        <span className="truncate mr-2">{buttonText}</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 flex-shrink-0" />
      </Button>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname.includes('analytics') ? 'analytics' : 'overview';

  const cards = [
    {
      title: "Products",
      description: "Add, edit, or remove products from your inventory",
      buttonText: "Manage Products",
      icon: <Package className="h-5 w-5" />,
      onClick: () => navigate("/admin/products")
    },
    {
      title: "Orders",
      description: "Track order status and manage customer orders",
      buttonText: "View Orders",
      icon: <ShoppingCart className="h-5 w-5" />,
      onClick: () => navigate("/admin/orders")
    },
    {
      title: "Reviews",
      description: "Moderate and respond to customer reviews",
      buttonText: "Manage Reviews",
      icon: <Star className="h-5 w-5" />,
      onClick: () => navigate("/admin/reviews")
    }
  ];

  return (
    <div className="w-full space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">
          Manage your store's products, orders, and customer interactions
        </p>
      </div>

      <Tabs defaultValue={activeTab} className="space-y-4 sm:space-y-6">
        <div className="overflow-x-auto">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger 
              value="overview" 
              onClick={() => navigate('/admin/dashboard')}
              className="text-xs sm:text-sm"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="analytics" 
              onClick={() => navigate('/admin/dashboard/analytics')}
              className="text-xs sm:text-sm"
            >
              <BarChart2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          {/* Stats Cards */}
          <div className="space-y-3">
            <h2 className="text-lg sm:text-xl font-semibold px-1">Overview</h2>
            <DashboardStats />
          </div>
          
          {/* Quick Actions */}
          <div className="space-y-3">
            <h2 className="text-lg sm:text-xl font-semibold px-1">Quick Actions</h2>
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((card, index) => (
                <DashboardCard
                  key={index}
                  title={card.title}
                  description={card.description}
                  buttonText={card.buttonText}
                  icon={card.icon}
                  onClick={card.onClick}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="w-full">
          <AdminAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
